// chat/directPeers — who may appear in a person's Direct (one-to-one) chat list.
//
// MIRRORED FILE: byte-identical in DispatchSystem/services/chat/ and
// wolfdriver-mobile/services/chat/. Imports nothing. See MIRROR.md.
//
// Rule (client, 2026-10-12): a DRIVER deals only with the office and the field — super admin, admin,
// dispatch, store and other drivers — not the rest of the team (managers, warehouse, packaging,
// team members such as developers and SEO, ...).
// The rule is TWO-WAY (2026-10-12): whoever a driver is not offered is not offered drivers either.
// One-way, a hidden person could still message a driver: the driver's phone rang for a chat that was
// in no list and carried no badge, and the sender believed it had been read.
// Everyone else is unchanged: any two non-drivers may message each other.
// This decides who is LISTED and who a link may open; it is not an access control (the database
// still lets any signed-in account read `users`), the same as the sidebar's role lists.
//
// Roles are plain strings on purpose: this folder cannot import `types`.

const DRIVER_PEER_ROLES: ReadonlySet<string> = new Set(['owner', 'super_admin', 'admin', 'dispatch', 'store', 'driver']);

/** May someone with role `myRole` have a Direct chat with someone with role `theirRole`? Symmetric. */
export function canDirectMessage(myRole: string | null | undefined, theirRole: string | null | undefined): boolean {
  if (myRole === 'driver') return DRIVER_PEER_ROLES.has(String(theirRole ?? ''));
  if (theirRole === 'driver') return DRIVER_PEER_ROLES.has(String(myRole ?? ''));
  return true;
}

/** The people to list for `me`: not archived, not me, and nobody `canDirectMessage` rules out. One pass. */
export function directPeers<T extends { id: string; role?: string | null; isArchived?: boolean | null }>(
  users: readonly T[],
  meId: string | null | undefined,
  myRole: string | null | undefined,
): T[] {
  const out: T[] = [];
  for (const u of users) {
    if (u.isArchived || u.id === meId) continue;
    if (!canDirectMessage(myRole, u.role)) continue;
    out.push(u);
  }
  return out;
}
